import React from 'react'
import { calculateDistance } from '../../../hooks/calculateDistance'

const TrackingSidebar = ({
  active,
  onClose,
  isTracking,
  setIsTracking,
  showOtherUsers,
  setShowOtherUsers,
  timer,
  isPaused,
  setIsPaused,
  startTracking,
  pauseTracking,
  resumeTracking,
  stopTracking,
  trackingPath,
  currentLocation,
  trackingPins,
  onResetTracking,
  onAddPin,
  onCancelTracking,
  activeTrackId,
  hasActiveTrack, // Add this new prop
  trackingSessions = []
}) => {
  const getTotalSessionsInfo = () => {
    if (trackingSessions.length === 0) return null

    const totalDistance = trackingSessions.reduce((sum, session) => {
      return sum + calculateDistance(session.path)
    }, 0)

    const totalPoints = trackingSessions.reduce((sum, session) => {
      return sum + session.path.length
    }, 0)

    return {
      totalSessions: trackingSessions.length,
      totalDistance,
      totalPoints
    }
  }

  const totalSessionsInfo = getTotalSessionsInfo()
  const toggleShowOtherUsers = () => {
    setShowOtherUsers(!showOtherUsers)
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs
      .toString()
      .padStart(2, '0')}`
  }

  const getTrackingSessionInfo = () => {
    if (!trackingPins.startPin || !trackingPins.stopPin) return null

    if (activeTrackId && trackingPins.stopPin.trackId !== activeTrackId) {
      return null
    }

    const startTime = new Date(trackingPins.startPin.timestamp)
    const stopTime = new Date(trackingPins.stopPin.timestamp)
    const duration = Math.round((stopTime - startTime) / 1000)
    const distance = calculateDistance(trackingPath)

    return {
      startTime: startTime.toLocaleTimeString(),
      stopTime: stopTime.toLocaleTimeString(),
      duration: formatTime(duration),
      distance: distance,
      startLocation: trackingPins.startPin.position,
      stopLocation: trackingPins.stopPin.position,
      totalPoints: trackingPath.length
    }
  }

  const sessionInfo = getTrackingSessionInfo()

  const hasCompletedTrack =
    trackingPins.startPin &&
    trackingPins.stopPin &&
    (!activeTrackId || trackingPins.stopPin.trackId === activeTrackId)

  // NEW: Check if user has an active track that needs to be resumed
  const shouldShowResumeOptions = hasActiveTrack && !isTracking

  const LocationInfo = () => {
    if (!currentLocation) return null

    const startLocation = trackingPath[0]
    const endLocation = trackingPath[trackingPath.length - 1]

    return (
      <div
        style={{
          padding: '12px',
          borderRadius: '4px',
          fontSize: '14px',
          fontWeight: '500',
          border: '1px solid #2e409e'
        }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '10px',
            color: isTracking ? 'green' : 'black'
          }}>
          <div>
            <i className='fa-solid fa-location-dot'></i>
          </div>
          <strong style={{ color: isTracking ? 'green' : 'black' }}>
            {isTracking ? 'ACTIVE TRACKING' : 'CURRENT LOCATION'}
          </strong>
        </div>

        <div className='mb-2 last:mb-0'>
          <strong>Now:</strong> {currentLocation.lat.toFixed(6)},{' '}
          {currentLocation.lng.toFixed(6)}
        </div>

        {isTracking && trackingPath.length > 0 && (
          <div className='space-y-2'>
            <div>
              <strong>Started:</strong> {startLocation.lat.toFixed(6)},{' '}
              {startLocation.lng.toFixed(6)}
            </div>
            <div>
              <strong>Points:</strong> {trackingPath.length}
            </div>
            <div>
              <strong>Distance:</strong> {calculateDistance(trackingPath)}m
            </div>
          </div>
        )}

        {!isTracking && trackingPath.length > 0 && (
          <div
            style={{
              padding: '8px 10px',
              background: '#e8f5e8',
              borderRadius: '3px',
              color: '#000'
            }}>
            <strong>Last Track:</strong> {trackingPath.length} points,{' '}
            {calculateDistance(trackingPath)}m
          </div>
        )}
      </div>
    )
  }

  const TrackingHistoryInfo = () => {
    if (!sessionInfo) return null

    return (
      <div
        style={{
          marginBottom: '15px',
          padding: '15px',
          background: '#f8f9fa',
          borderRadius: '8px',
          border: '2px solid #00d084'
        }}>
        <h3
          style={{
            margin: '0 0 10px 0',
            fontSize: '16px',
            fontWeight: 'bold',
            color: '#00d084'
          }}>
          📊 Your Tracking Session Summary
        </h3>

        <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
          <div>
            <strong>Start Time:</strong> {sessionInfo.startTime}
          </div>
          <div>
            <strong>Stop Time:</strong> {sessionInfo.stopTime}
          </div>
          <div>
            <strong>Duration:</strong> {sessionInfo.duration}
          </div>
          <div>
            <strong>Distance:</strong> {sessionInfo.distance}m
          </div>
          <div>
            <strong>Points Recorded:</strong> {sessionInfo.totalPoints}
          </div>
          <div>
            <strong>Start Location:</strong>
            <br />
            {sessionInfo.startLocation.lat.toFixed(6)},{' '}
            {sessionInfo.startLocation.lng.toFixed(6)}
          </div>
          <div>
            <strong>End Location:</strong>
            <br />
            {sessionInfo.stopLocation.lat.toFixed(6)},{' '}
            {sessionInfo.stopLocation.lng.toFixed(6)}
          </div>
        </div>

        <button
          onClick={onResetTracking}
          style={{
            width: '100%',
            marginTop: '10px',
            padding: '8px',
            background: '#eb144c',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: 'bold',
            transition: 'background 0.3s ease'
          }}
          onMouseOver={(e) => (e.target.style.background = '#d01344')}
          onMouseOut={(e) => (e.target.style.background = '#eb144c')}>
          <i
            className='fa-regular fa-trash-can'
            style={{ marginRight: '6px' }}
          />
          Reset Your Tracking Path
        </button>
      </div>
    )
  }

  if (!active) return null

  return (
    <>
      {/* Overlay */}
      <div
        className='sidebar-overlay'
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 9998
        }}
      />

      {/* Sidebar */}
      <div
        className='tracking-sidebar'
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '350px',
          maxWidth: '90vw',
          background: '#fff',
          boxShadow: '-2px 0 10px rgba(0,0,0,0.3)',
          zIndex: 9999,
          transform: active ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s ease',
          display: 'flex',
          flexDirection: 'column',
          padding: '20px'
        }}>
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            paddingBottom: '15px',
            borderBottom: '1px solid #eee'
          }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold' }}>
              {isTracking
                ? 'Your Live Tracking'
                : shouldShowResumeOptions
                ? 'Resume Your Tracking'
                : 'Your Tracking'}
            </h2>
            {totalSessionsInfo && (
              <div
                style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                {totalSessionsInfo.totalSessions} sessions •{' '}
                {totalSessionsInfo.totalPoints} points •{' '}
                {totalSessionsInfo.totalDistance}m
              </div>
            )}
            {shouldShowResumeOptions && (
              <div
                style={{
                  fontSize: '12px',
                  color: '#ff6900',
                  marginTop: '2px',
                  fontWeight: 'bold'
                }}>
                You have an active tracking session
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              color: '#666',
              padding: '5px',
              borderRadius: '50%',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
            <i className='fa fa-times' />
          </button>
        </div>

        {/* Resume Active Track Section */}
        {shouldShowResumeOptions && (
          <div
            style={{
              marginBottom: '20px',
              padding: '15px',
              background: '#fff3e0',
              borderRadius: '8px',
              border: '2px solid #ff6900'
            }}>
            <h3
              style={{
                margin: '0 0 10px 0',
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#ff6900'
              }}>
              ⚠️ Active Tracking Session Found
            </h3>
            <p style={{ fontSize: '14px', margin: '0 0 15px 0' }}>
              You have an active tracking session. You can resume it from your
              current location.
            </p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={resumeTracking}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: '#ff6900',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}>
                <i className='fa fa-play' style={{ marginRight: '8px' }} />
                Resume Tracking
              </button>
              <button
                onClick={onCancelTracking}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'transparent',
                  color: '#ff6900',
                  border: '1px solid #ff6900',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}>
                <i className='fa fa-times' style={{ marginRight: '8px' }} />
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* {hasCompletedTrack && <TrackingHistoryInfo />} */}

        {/* Timer Display */}
        {isTracking && (
          <div
            style={{
              textAlign: 'center',
              fontSize: '32px',
              fontWeight: 'bold',
              marginBottom: '30px',
              color: isPaused ? '#ff6900' : '#00d084',
              padding: '20px',
              background: '#f8f9fa',
              borderRadius: '8px'
            }}>
            {formatTime(timer)}
          </div>
        )}

        {/* Location Info */}
        <LocationInfo />

        {/* Controls Section */}
        <div style={{ flex: 1 }}>
          {/* Show Other Users Toggle Switch */}
          <div style={{ marginBottom: '25px' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 0'
              }}>
              <div style={{ fontSize: '16px', fontWeight: '500' }}>
                Show other users' tracks
              </div>
              <button
                onClick={toggleShowOtherUsers}
                style={{
                  position: 'relative',
                  width: '50px',
                  height: '24px',
                  background: showOtherUsers ? '#00d084' : '#ccc',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'background 0.3s ease',
                  outline: 'none'
                }}>
                <div
                  style={{
                    position: 'absolute',
                    top: '2px',
                    left: showOtherUsers ? '26px' : '2px',
                    width: '20px',
                    height: '20px',
                    background: 'white',
                    borderRadius: '50%',
                    transition: 'left 0.3s ease',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                  }}
                />
              </button>
            </div>
          </div>

          {/* Control Buttons */}
          {isTracking && (
            <div>
              {/* <button
                onClick={onAddPin}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#2e409e',
                  color: 'white',
                  fontWeight: '500',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  transition: 'background 0.3s ease',
                  marginBottom: '10px'
                }}
                onMouseOver={(e) => (e.target.style.background = '#1a32b1')}
                onMouseOut={(e) => (e.target.style.background = '#2e409e')}>
                <i
                  className='fa fa-map-marker-alt'
                  style={{ marginRight: '8px' }}
                />
                Add Pin to Your Location
              </button> */}
            </div>
          )}

          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {!isTracking && !shouldShowResumeOptions ? (
              <>
                {/* START TRACKING BUTTON - Only show when no active track */}
                <button
                  onClick={startTracking}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '16px',
                    fontWeight: '600',
                    background: '#2e409e',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'background 0.3s ease'
                  }}
                  onMouseOver={(e) => (e.target.style.background = '#1a32b1')}
                  onMouseOut={(e) => (e.target.style.background = '#2e409e')}>
                  <i className='fa fa-play' style={{ marginRight: '8px' }} />
                  Start New Tracking
                </button>

                {/* Cancel button only shows if user has unsaved tracking data */}
                {trackingPath.length > 0 && !hasCompletedTrack && (
                  <button
                    onClick={onCancelTracking}
                    style={{
                      width: '100%',
                      padding: '12px',
                      color: '#2e409e',
                      fontWeight: '600',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      border: '1px solid #2e409e',
                      transition: 'border 0.3s ease'
                    }}
                    onMouseOver={(e) =>
                      (e.target.style.borderColor = '#1a32b1')
                    }
                    onMouseOut={(e) =>
                      (e.target.style.borderColor = '#2e409e')
                    }>
                    <i className='fa fa-times' style={{ marginRight: '8px' }} />
                    Cancel Your Tracking
                  </button>
                )}
              </>
            ) : isTracking ? (
              <>
                {/* Active tracking controls */}
                <div style={{ display: 'flex', gap: '10px' }}>
                  {!isPaused ? (
                    <button
                      onClick={pauseTracking}
                      style={{
                        flex: 1,
                        padding: '12px',
                        background: '#2e409e',
                        color: 'white',
                        fontWeight: '500',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        transition: 'background 0.3s ease'
                      }}
                      onMouseOver={(e) =>
                        (e.target.style.background = '#1a32b1')
                      }
                      onMouseOut={(e) =>
                        (e.target.style.background = '#2e409e')
                      }>
                      <i
                        className='fa fa-pause'
                        style={{ marginRight: '8px' }}
                      />
                      Pause
                    </button>
                  ) : (
                    <button
                      onClick={resumeTracking}
                      style={{
                        flex: 1,
                        padding: '12px',
                        background: '#2e409e',
                        color: 'white',
                        fontWeight: '500',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        transition: 'background 0.3s ease'
                      }}
                      onMouseOver={(e) =>
                        (e.target.style.background = '#1a32b1')
                      }
                      onMouseOut={(e) =>
                        (e.target.style.background = '#2e409e')
                      }>
                      <i
                        className='fa fa-play'
                        style={{ marginRight: '8px' }}
                      />
                      Resume
                    </button>
                  )}

                  <button
                    onClick={onCancelTracking}
                    style={{
                      flex: 1,
                      padding: '12px',
                      color: '#2e409e',
                      border: 'none',
                      fontWeight: '500',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      border: '1px solid #2e409e',
                      transition: 'border 0.3s ease'
                    }}
                    onMouseOver={(e) =>
                      (e.target.style.borderColor = '#1a32b1')
                    }
                    onMouseOut={(e) =>
                      (e.target.style.borderColor = '#2e409e')
                    }>
                    <i className='fa fa-times' style={{ marginRight: '8px' }} />
                    Cancel
                  </button>
                </div>
                <button
                  onClick={stopTracking}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    background: '#eb144c',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'background 0.3s ease'
                  }}
                  onMouseOver={(e) => (e.target.style.background = '#d01344')}
                  onMouseOut={(e) => (e.target.style.background = '#eb144c')}>
                  <i className='fa fa-stop' style={{ marginRight: '8px' }} />
                  Stop Your Tracking
                </button>
              </>
            ) : null}
          </div>
        </div>

        {/* Status Footer */}
        <div
          style={{
            marginTop: 'auto',
            padding: '15px',
            background: isTracking
              ? isPaused
                ? '#fff3e0'
                : '#e8f5e8'
              : shouldShowResumeOptions
              ? '#fff3e0'
              : hasCompletedTrack
              ? '#e8f5e8'
              : '#f5f5f5',
            borderRadius: '8px',
            textAlign: 'center',
            fontSize: '14px',
            fontWeight: 'bold',
            color: isTracking
              ? isPaused
                ? '#ff6900'
                : '#00d084'
              : shouldShowResumeOptions
              ? '#ff6900'
              : hasCompletedTrack
              ? '#00d084'
              : '#666',
            border: `2px solid ${
              isTracking
                ? isPaused
                  ? '#ff6900'
                  : '#00d084'
                : shouldShowResumeOptions
                ? '#ff6900'
                : hasCompletedTrack
                ? '#00d084'
                : '#ddd'
            }`
          }}>
          <i
            className={`fa ${
              isTracking
                ? isPaused
                  ? 'fa-pause'
                  : 'fa-play'
                : shouldShowResumeOptions
                ? 'fa-exclamation-triangle'
                : hasCompletedTrack
                ? 'fa-check'
                : 'fa-circle-o'
            } `}
            style={{ marginRight: '8px' }}
          />
          {isTracking
            ? isPaused
              ? 'Your Tracking Paused'
              : 'Your Tracking Active'
            : shouldShowResumeOptions
            ? 'Active Session - Resume Available'
            : hasCompletedTrack
            ? 'Your Tracking Completed'
            : 'Ready to Start Your Tracking'}
        </div>
      </div>
    </>
  )
}

export default TrackingSidebar
